/* Reconcile ordinary Shopify tier SKUs with the actual repeated engraving. */
(() => {
  'use strict';
  if (globalThis.JaxBulkCart) return;
  const SOURCE = 'bulk-builder-v1', JOURNAL = 'jax:bulk-cart-remap:v1';
  const TOKEN = '_Studio submission', HASH = '_Jax bulk design';
  const percentages = { 10: 5, 24: 6, 48: 8, 100: 10 };
  const copy = value => JSON.parse(JSON.stringify(value));
  const fail = code => { throw Object.assign(new Error(code), { code }); };
  const property = (line, name) => line.properties?.[`_${name}`] ?? line.properties?.[name];
  // Shopify can omit empty personalization fields when an existing multipart
  // line is recreated with JSON. Compare every meaningful value exactly.
  const propertyEntries = value => Object.entries(value || {}).filter(([, item]) => item !== '' && item !== null && item !== undefined).sort();
  const same = (a, b) => JSON.stringify(propertyEntries(a)) === JSON.stringify(propertyEntries(b));
  const variant = line => String(line.variant_id ?? line.id);
  const fingerprint = cart => JSON.stringify(cart.items.map(line => [line.key, variant(line), line.quantity, line.final_line_price, Object.entries(line.properties || {}).sort()]));
  const threshold = quantity => [10, 24, 48, 100].filter(value => quantity >= value).at(-1) || 0;
  function attachment(value) {
    if (typeof value !== 'string' || value.length > 1024 || /[\s\\%]/.test(value) || value.includes('..')) fail('artwork');
    let url; try { url = new URL(value); } catch { fail('artwork'); }
    if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com' || url.port || url.username || url.password || url.hash ||
        !/^\/s\/files\/1\/0682\/4322\/6877\/uploads\/[A-Za-z0-9_-][A-Za-z0-9_.-]{0,199}\.json$/.test(url.pathname) ||
        [...url.searchParams].some(([key, item]) => key !== 'v' || !/^\d{1,20}$/.test(item)) || url.searchParams.getAll('v').length > 1) fail('artwork');
    return url.href;
  }
  // Older mobile browsers have AbortController but no AbortSignal.timeout.
  function requestDeadline(milliseconds) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return { signal: AbortSignal.timeout(milliseconds), clear() {} };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), milliseconds);
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
  }
  function createClient({ runtime = globalThis.JaxBulkCheckout, eligibility = globalThis.JaxBulkEligibility,
    fetchImpl = fetch, storage = globalThis.sessionStorage, cryptoImpl = crypto, root = '/' } = {}) {
    const base = /^\/(?:[A-Za-z0-9_-]+\/)*$/.test(root) ? root : '/';
    let busy = false;
    const cache = new Map();
    const originalId = id => runtime.originalVariantId(String(id)) || String(id);
    const mapping = () => runtime.mapping || runtime.catalog;
    const record = id => mapping()?.variants?.[originalId(id)];
    const relevant = line => Boolean(record(variant(line)) && (originalId(variant(line)) !== variant(line) || property(line, 'Jax bulk source') === SOURCE));
    async function request(path, options = {}, maximum = 2 * 1024 * 1024) {
      const deadline = requestDeadline(20000);
      try {
        const response = await fetchImpl(path.startsWith('https:') ? path : `${base}${path}`, {
          credentials: path.startsWith('https:') ? 'omit' : 'same-origin', cache: 'no-store', redirect: 'error',
          signal: deadline.signal, ...options,
          headers: { Accept: 'application/json', ...options.headers }
        });
        if (response.redirected || Number(response.headers.get('content-length') || 0) > maximum) fail('unavailable');
        const reader = response.body?.getReader(); if (!reader) fail('unavailable');
        const chunks = []; let size = 0;
        try { for (;;) { const part = await reader.read(); if (part.done) break; size += part.value.length; if (size > maximum) { await reader.cancel(); fail('unavailable'); } chunks.push(part.value); } }
        finally { reader.releaseLock(); }
        const bytes = new Uint8Array(size); let offset = 0;
        for (const part of chunks) { bytes.set(part, offset); offset += part.length; }
        let data; try { data = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); } catch { fail('unavailable'); }
        return { ok: response.ok, status: response.status, data };
      } finally { deadline.clear(); }
    }
    async function read() {
      const response = await request('cart.js');
      const cart = response.data;
      if (!response.ok || !Array.isArray(cart?.items) || cart.items.length > 1000 || typeof cart.currency !== 'string' ||
          cart.items.some(line => !line?.key || !Number.isSafeInteger(line.quantity) || line.quantity < 1) ||
          new Set(cart.items.map(line => line.key)).size !== cart.items.length) fail('unavailable');
      return cart;
    }
    async function design(line) {
      const url = attachment(property(line, 'Design file'));
      if (!cache.has(url)) cache.set(url, request(url, {}, 32 * 1024 * 1024).then(result => {
        if (!result.ok) fail('artwork'); return result.data;
      }).catch(error => { cache.delete(url); throw error; }));
      const document = await cache.get(url), entry = record(variant(line));
      if (!entry || originalId(document.variantId) !== originalId(variant(line)) ||
          ![String(entry.originalProductId), String(entry.productId)].includes(String(document.productId))) fail('artwork');
      const normalized = { ...document, productId: String(entry.originalProductId), variantId: originalId(variant(line)) };
      if (!runtime.classify(normalized).eligible) fail('artwork');
      return normalized;
    }
    async function analyze(cart, extra = null) {
      await runtime.ready;
      if (!mapping()?.variants) fail('configuration');
      if (cart.currency !== 'USD' && (extra || cart.items.some(relevant))) fail('configuration');
      const rows = [], groups = new Map();
      for (const line of cart.items.filter(relevant)) {
        if (extra?.source && line.key === extra.source.key) continue;
        if (line.quantity > 999 || property(line, 'Studio second side') === 'true') fail('quote');
        const document = await design(line), key = await eligibility.designKey(document);
        if (!key) fail('artwork');
        const row = { line, document, key, originalVariantId: originalId(variant(line)) };
        rows.push(row); groups.set(key, (groups.get(key) || 0) + line.quantity);
      }
      if (extra) {
        if (!runtime.classify(extra.document).eligible || !Number.isSafeInteger(extra.quantity) || extra.quantity < 1 || extra.quantity > 999) fail('artwork');
        if (extra.source && !cart.items.some(line => line.key === extra.source.key && line.quantity === extra.source.quantity && variant(line) === String(extra.source.variantId))) fail('changed');
        const key = await eligibility.designKey(extra.document); if (!key) fail('artwork');
        groups.set(key, (groups.get(key) || 0) + extra.quantity);
        rows.push({ document: extra.document, key, originalVariantId: originalId(extra.document.variantId), extra: true });
      }
      for (const row of rows) {
        row.groupQuantity = groups.get(row.key); if (row.groupQuantity > 999) fail('quote');
        row.threshold = threshold(row.groupQuantity); row.percent = percentages[row.threshold] || 0;
        const entry = record(row.originalVariantId); if (!entry) fail('configuration');
        row.targetId = row.threshold ? String(entry.tiers[row.threshold] || '') : row.originalVariantId;
        if (!/^[1-9]\d+$/.test(row.targetId)) fail('configuration');
        row.basePrice = entry.basePrice;
      }
      const changes = rows.filter(row => !row.extra && variant(row.line) !== row.targetId);
      let pricesValid = true;
      for (const row of rows.filter(row => !row.extra && row.threshold && variant(row.line) === row.targetId)) {
        const line = row.line;
        // Verified against the store's native automatic rules: Shopify floors
        // the discount in cents on each cup, then multiplies by the quantity.
        const nativeUnit = row.basePrice - Math.floor(row.basePrice * row.percent / 100);
        const allocations = line.line_level_discount_allocations || [];
        const matching = allocations.some(allocation => allocation.discount_application?.value_type === 'percentage' && Number(allocation.discount_application.value) === row.percent && allocation.discount_application.target_type === 'line_item');
        if (line.original_price !== row.basePrice || line.final_line_price !== nativeUnit * line.quantity ||
            line.final_price !== undefined && line.final_price !== nativeUnit || !matching) pricesValid = false;
      }
      return { relevant: rows.length > 0, rows, changes, pricesValid, ready: changes.length === 0 && pricesValid, cart };
    }
    function persist(value) {
      const raw = JSON.stringify(value); if (raw.length > 32768) fail('unavailable');
      storage.setItem(JOURNAL, raw); if (storage.getItem(JOURNAL) !== raw) fail('unavailable');
    }
    function pending() {
      let raw; try { raw = storage.getItem(JOURNAL); } catch { fail('uncertain'); }
      if (!raw) return null;
      try {
        const item = JSON.parse(raw);
        if (raw.length > 32768 || item.version !== 1 || !/^[a-f0-9-]{36}$/.test(item.token) || !['adding', 'removing'].includes(item.stage) ||
            !item.source?.key || !item.source.properties?.[TOKEN] || !/^[1-9]\d+$/.test(item.targetId) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 999 || item.properties?.[TOKEN] !== item.token) fail('uncertain');
        return item;
      } catch { fail('uncertain'); }
    }
    function clear() { storage.removeItem(JOURNAL); if (storage.getItem(JOURNAL)) fail('uncertain'); }
    const target = (cart, item) => {
      const lines = cart.items.filter(line => line.properties?.[TOKEN] === item.token);
      if (lines.length !== 1 || variant(lines[0]) !== item.targetId || lines[0].quantity !== item.quantity || !same(lines[0].properties, item.properties)) fail('uncertain');
      return lines[0];
    };
    const oldLine = (cart, item) => {
      const lines = cart.items.filter(line => line.properties?.[TOKEN] === item.source.properties[TOKEN]);
      if (!lines.length) {
        if (cart.items.some(line => line.properties?.[TOKEN] !== item.token && property(line, 'Design file') === property(item.source, 'Design file'))) fail('changed');
        return null;
      }
      if (lines.length !== 1 || variant(lines[0]) !== item.source.variantId || lines[0].quantity !== item.quantity || !same(lines[0].properties, item.source.properties)) fail('changed');
      return lines[0];
    };
    async function finish(item, cart = null) {
      cart ||= await read();
      const found = cart.items.filter(line => line.properties?.[TOKEN] === item.token);
      if (!found.length && item.rejected === true && item.stage === 'adding') { clear(); fail('rejected'); }
      target(cart, item);
      const old = oldLine(cart, item);
      if (!old) { clear(); return cart; }
      if (item.stage === 'removing') fail('uncertain');
      item.stage = 'removing'; persist(item);
      try { await request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: old.key, quantity: 0 }) }); } catch { /* Read back; never replay an uncertain removal. */ }
      cart = await read(); target(cart, item); if (oldLine(cart, item)) fail('uncertain');
      clear(); return cart;
    }
    async function remap(row) {
      const token = cryptoImpl.randomUUID(), properties = { ...row.line.properties, [TOKEN]: token, [HASH]: row.key };
      const item = { version: 1, token, source: { key: row.line.key, variantId: variant(row.line), quantity: row.line.quantity, properties: copy(row.line.properties) },
        targetId: row.targetId, quantity: row.line.quantity, properties, stage: 'adding', rejected: false };
      if (!item.source.properties[TOKEN]) fail('artwork');
      persist(item);
      try {
        const response = await request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: item.targetId, quantity: item.quantity, properties }] }) });
        if ([400, 404, 422].includes(response.status) && response.data?.status === response.status && (typeof response.data.message === 'string' || typeof response.data.description === 'string')) { item.rejected = true; persist(item); }
      } catch { /* The token journal prevents duplicate additions after timeout. */ }
      return finish(item);
    }
    return {
      read, analyze, get busy() { return busy; },
      async prepareForm(form, document, quantity, { source = null } = {}) {
        if (!runtime.enabled || pending()) fail('unavailable');
        const state = await analyze(await read(), { document, quantity, source });
        const row = state.rows.find(row => row.extra);
        form.set('id', row.targetId);
        for (const [key, value] of Object.entries(runtime.classify(document).properties)) form.set(`properties[${key}]`, value);
        form.set(`properties[${HASH}]`, row.key);
        return { variantId: row.targetId, document };
      },
      async reconcile() {
        if (busy) fail('busy'); busy = true;
        try {
          if (!runtime.enabled) fail('configuration');
          await runtime.ready; let changed = false;
          const journal = pending(); let cart = journal ? await finish(journal) : await read();
          if (journal) changed = true;
          for (let index = 0; index <= 1000; index++) {
            const state = await analyze(cart);
            if (!state.changes.length) return { ...state, changed };
            cart = await remap(state.changes[0]); changed = true;
          }
          fail('unavailable');
        } finally { busy = false; }
      }
    };
  }
  const api = { createClient, threshold, attachment, fingerprint, readyForCheckout: false, checkingCheckout: false };
  globalThis.JaxBulkCart = api;
  if (typeof document === 'undefined') return;
  let client, clientRuntime, task, state, pass = null, timer;
  const buttons = () => [...document.querySelectorAll('[name="checkout"]')];
  const quantityPending = () => [...document.querySelectorAll('cart-items,cart-drawer-items')].some(node => node.quantityUpdatePending);
  const formMatches = form => {
    if (!form || !state?.cart) return false;
    const rows = [...form.querySelectorAll('[data-line-key]')], seen = new Set();
    return rows.length === state.cart.items.length && rows.every(row => {
      const item = state.cart.items.find(line => line.key === row.dataset.lineKey), input = row.querySelector('[name="updates[]"]');
      if (!item || seen.has(item.key) || !input || !String(input.value).trim() || Number(input.value) !== item.quantity) return false;
      seen.add(item.key); return true;
    });
  };
  const runtime = () => globalThis.JaxBulkCheckout;
  const getClient = () => {
    if (!client || clientRuntime !== runtime()) { clientRuntime = runtime(); client = createClient({ runtime: clientRuntime, root: globalThis.Shopify?.routes?.root || '/' }); }
    return client;
  };
  const strings = { checking: 'Updating your bulk pricing…', unavailable: 'We could not confirm your bulk pricing. Please check it again before checkout.', quote: 'Orders of 1,000 or more cups need a custom quote.', artwork: 'Review this bulk design before checkout.', configuration: 'Bulk checkout is being prepared. Please request a quote for now.' };
  function paint(blocked, message = '') {
    api.readyForCheckout = !blocked;
    for (const button of buttons()) {
      if (blocked) { button.dataset.bulkOrderBlocked = 'true'; button.disabled = true; }
      else if (button.dataset.bulkOrderBlocked === 'true') { delete button.dataset.bulkOrderBlocked; button.disabled = button.dataset.studioApprovalBlocked === 'true' || button.dataset.studioUpgradeBlocked === 'true' || !state?.cart?.items.length; }
      let note = button.parentElement?.querySelector('[data-bulk-cart-status]');
      if (!note && button.parentElement) { note = document.createElement('div'); note.dataset.bulkCartStatus = ''; note.setAttribute('role', 'status'); button.parentElement.append(note); }
      if (note) { note.hidden = !blocked; note.replaceChildren(); if (blocked) { const text = document.createElement('p'); text.textContent = message || strings.checking; note.append(text); if (!task) { const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'button button--secondary'; retry.textContent = 'Check bulk pricing'; retry.addEventListener('click', () => refresh()); note.append(retry); } } }
    }
    for (const node of document.querySelectorAll('[data-studio-approval-accelerated],[data-studio-upgrade-accelerated]')) {
      if (blocked) { node.dataset.bulkOrderHidden = 'true'; node.hidden = true; node.inert = true; }
      else if (node.dataset.bulkOrderHidden === 'true') { delete node.dataset.bulkOrderHidden; node.hidden = node.dataset.studioApprovalHidden === 'true' || buttons().some(button => button.dataset.studioUpgradeBlocked === 'true'); node.inert = node.hidden; }
    }
  }
  async function refresh() {
    if (task) return task;
    pass = null; paint(true);
    task = (async () => {
      const current = await getClient().read();
      // The native bulk service's product ID is a fail-closed fallback when
      // its catalog cannot load. Ordinary carts do not depend on that catalog.
      const productId = String(runtime()?.mapping?.bulkProductId || '15443114164477');
      const relevant = current.items.some(line => property(line, 'Jax bulk source') === SOURCE || String(line.product_id) === productId || runtime()?.originalVariantId?.(variant(line)) !== variant(line) && runtime()?.originalVariantId?.(variant(line)));
      if (!relevant) { state = { cart: current, ready: true, relevant: false }; return state; }
      await runtime()?.ready;
      if (quantityPending()) fail('busy');
      state = await getClient().reconcile();
      if (state.changed) { location.reload(); return state; }
      if (!state.ready) fail('unavailable');
      globalThis.StudioCartApproval?.accept(state.cart);
      globalThis.StudioCartUpgrades?.accept(state.cart);
      return state;
    })().catch(error => { state = { ...state, ready: false, error: strings[error.code] || strings.unavailable }; return state; }).finally(() => {
      task = null; paint(!state?.ready, state?.error);
    });
    return task;
  }
  Object.assign(api, { refresh, prepareForm: (...args) => getClient().prepareForm(...args), invalidate: () => { pass = null; paint(true); }, finishCheckout: () => { pass = null; } });
  window.addEventListener('submit', async event => {
    if (!['cart', 'CartDrawer-Form'].includes(event.target.id) || event.submitter?.name !== 'checkout') return;
    if (pass && state?.ready && pass === fingerprint(state.cart) && !quantityPending() && formMatches(event.target)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    api.checkingCheckout = true;
    try {
      const checked = await refresh(); if (!checked?.ready || checked.changed || quantityPending() || !formMatches(event.target)) return;
      pass = fingerprint(checked.cart);
      const quantities = [...event.target.querySelectorAll('[name="updates[]"]')].filter(input => !input.disabled);
      quantities.forEach(input => { input.disabled = true; });
      try { event.target.requestSubmit(event.submitter); }
      finally { quantities.forEach(input => { input.disabled = false; }); }
    } finally {
      api.checkingCheckout = false;
      if (!globalThis.StudioCartUpgrades?.checkingCheckout) { pass = null; globalThis.StudioCartApproval?.finishCheckout(); }
    }
  }, true);
  document.addEventListener('input', event => { if (!event.target.matches?.('[name="updates[]"]')) return; pass = null; paint(true); clearTimeout(timer); timer = setTimeout(refresh, 700); }, true);
  function boot() {
    if (buttons().length) refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, () => { if (buttons().length) refresh(); });
  }
  window.addEventListener('pageshow', event => { if (event.persisted && buttons().length) refresh(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();
